import { useCallback, useEffect, useState } from 'react'
import { deleteNote, fetchMembers, fetchNotes, patchNote, postNote } from '../data/api'
import type { MemberLite, Note } from '../types'
import { SectionLabel } from './RoundTimeline'
import { fmtStamp } from '../data/fmt'
import { useCanCoach, useSession } from '../data/session'

// Review notes on a match (PLAN Phase 4). A note with a round number is the useful shape: it links
// straight into the replayer at that moment, so "watch your crossfire here" is one click from being
// watched rather than a sentence someone has to go and find.
//
// Assignments are notes with someone on the hook — coaches only, because that's homework, not a
// comment. Everything here is team-visible by design; the thing PLAN §3 keeps private is the AI's
// per-player feedback, not what the coach says to the team.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

export function Notes({
  demoId,
  rounds,
  onRound,
}: {
  demoId: string
  /** How many rounds the match actually had — a note pointing at round 99 is a dead link. */
  rounds?: number
  onRound?: (round: number) => void
}) {
  const { user } = useSession()
  const canCoach = useCanCoach()
  const [notes, setNotes] = useState<Note[] | null>(null)
  const [members, setMembers] = useState<MemberLite[]>([])
  const [err, setErr] = useState<string | null>(null)

  const [body, setBody] = useState('')
  const [round, setRound] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [asHomework, setAsHomework] = useState(false)
  const [staffOnly, setStaffOnly] = useState(false)
  const [busy, setBusy] = useState(false)
  // Editing in place: a coaching note with a typo in it gets quoted back for a season.
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  const load = useCallback(() => {
    fetchNotes(demoId)
      .then(setNotes)
      .catch((e) => setErr((e as Error).message))
  }, [demoId])

  useEffect(() => {
    load()
    fetchMembers().then(setMembers).catch(() => setMembers([]))
  }, [load])

  const nameOf = (sid: string) => members.find((m) => m.steamid64 === sid)?.name ?? sid.slice(-6)
  const toggle = (sid: string) =>
    setPicked((cur) => (cur.includes(sid) ? cur.filter((s) => s !== sid) : [...cur, sid]))

  // Checked here rather than in the API: the server would have to read the scoreboard on every
  // write to know the round count, and the cost of being wrong is a link that goes nowhere.
  const roundNum = Number(round)
  const roundBad = round !== '' && (!Number.isFinite(roundNum) || roundNum < 1 || (!!rounds && roundNum > rounds))

  const submit = async () => {
    if (!body.trim() || roundBad) return
    setBusy(true)
    setErr(null)
    try {
      const n = Number(round)
      await postNote(demoId, {
        body,
        ...(Number.isFinite(n) && n > 0 ? { round: n } : {}),
        // The same picker does both jobs — the checkbox decides whether the people you picked are
        // being told or being asked.
        ...(asHomework ? { assignees: picked } : { mentions: picked }),
        ...(staffOnly ? { staffOnly: true } : {}),
      })
      setBody('')
      setRound('')
      setPicked([])
      setStaffOnly(false)
      load()
    } catch (e) {
      setErr((e as Error).message === 'forbidden' ? 'Only coaches can assign work.' : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const tick = async (n: Note, done: boolean) => {
    await patchNote(demoId, n.id, { done }).catch((e) => setErr((e as Error).message))
    load()
  }

  const saveEdit = async (n: Note) => {
    if (draft.trim() && draft !== n.body) {
      await patchNote(demoId, n.id, { body: draft }).catch((e) => setErr((e as Error).message))
    }
    setEditing(null)
    load()
  }

  const remove = async (n: Note) => {
    if (!confirm('Delete this note?')) return
    await deleteNote(demoId, n.id).catch((e) => setErr((e as Error).message))
    load()
  }

  return (
    <div className="space-y-5">
      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="N0" label="Add a note" />
        <>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="We stack B on every eco — they read it by round 12."
              aria-label="Note"
              className="mt-3 w-full border border-border bg-bg-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
              style={{ borderRadius: 'var(--radius)' }}
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-text-faint">
                Round
                <input
                  value={round}
                  onChange={(e) => setRound(e.target.value.replace(/\D/g, ''))}
                  placeholder="—"
                  aria-label="Round number this note is about"
                  aria-invalid={roundBad}
                  inputMode="numeric"
                  className="w-14 border border-border bg-bg-2 px-2 py-1 text-center text-xs text-text outline-none"
                  style={{ borderRadius: 'var(--radius)' }}
                />
              </label>
              <span className={`text-[11px] ${roundBad ? 'text-loss' : 'text-text-faint'}`}>
                {roundBad
                  ? `this match had ${rounds} rounds`
                  : 'optional — a round makes it clickable straight into the replay'}
              </span>
              {canCoach && (
                <label className="ml-auto flex items-center gap-1.5 text-xs text-text-dim">
                  <input type="checkbox" checked={asHomework} onChange={(e) => setAsHomework(e.target.checked)} />
                  assign as homework
                </label>
              )}
              {canCoach && (
                <label className="flex items-center gap-1.5 text-xs text-text-dim">
                  <input type="checkbox" checked={staffOnly} onChange={(e) => setStaffOnly(e.target.checked)} />
                  staff only
                </label>
              )}
            </div>

            {members.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-text-faint">
                  {asHomework ? 'For' : 'Notify'}
                </span>
                {members.map((m) => (
                  <button
                    key={m.steamid64}
                    onClick={() => toggle(m.steamid64)}
                    aria-pressed={picked.includes(m.steamid64)}
                    aria-label={`${asHomework ? 'Assign to' : 'Notify'} ${m.name}`}
                    className="px-2 py-1 text-[11px]"
                    style={{
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--border)',
                      background: picked.includes(m.steamid64) ? 'var(--accent)' : 'transparent',
                      color: picked.includes(m.steamid64) ? 'var(--accent-ink)' : 'var(--text-dim)',
                    }}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            )}

            {err && (
              <p role="alert" className="mt-2 text-xs text-loss">
                {err}
              </p>
            )}
            <button
              onClick={submit}
              disabled={busy || !body.trim() || roundBad}
              className="font-display mt-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-50"
              style={{ borderRadius: 'var(--radius)', background: 'var(--accent)', color: 'var(--accent-ink)' }}
            >
              {busy ? 'Saving…' : asHomework ? 'Assign' : 'Post note'}
            </button>
        </>
      </section>

      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="N1" label="Notes on this match" />
        {!notes ? (
          <p className="mt-3 text-sm text-text-dim">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="mt-3 text-sm text-text-faint">
            Nothing yet. A note with a round number is worth five without one — it's the difference
            between "we're sloppy on eco" and a round somebody can watch.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {notes.map((n) => {
              const mine = n.assignees.includes(user?.steamid64 ?? '')
              const done = n.doneBy.includes(user?.steamid64 ?? '')
              return (
                <div
                  key={n.id}
                  className="border-l-2 pl-3"
                  style={{ borderColor: n.staffOnly ? 'var(--accent)' : 'var(--border)' }}
                >
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-display text-xs uppercase tracking-wider text-accent">{n.authorName}</span>
                    <span className="text-[11px] text-text-faint">{fmtStamp(n.createdAt)}</span>
                    {/* Whoever is writing needs to know at a glance who is in the room. A note the
                        author thinks is private but isn't is the failure that matters here. */}
                    {n.staffOnly && (
                      <span
                        title="Only coaches and admins can see this note"
                        className="rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                        style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}
                      >
                        staff only
                      </span>
                    )}
                    {n.round !== undefined && (
                      <button
                        onClick={() => onRound?.(n.round!)}
                        title="Open the replay at this round"
                        className="rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                        style={{ background: 'var(--border)', color: 'var(--text-dim)' }}
                      >
                        round {n.round} ▸
                      </button>
                    )}
                    {n.assignees.length > 0 && (
                      <span className="text-[10px] text-text-faint">
                        for {n.assignees.map(nameOf).join(', ')}
                        {n.doneBy.length > 0 && ` · done: ${n.doneBy.map(nameOf).join(', ')}`}
                      </span>
                    )}
                    {n.mentions.length > 0 && (
                      <span className="text-[10px] text-text-faint">→ {n.mentions.map(nameOf).join(', ')}</span>
                    )}
                    <span className="ml-auto flex gap-2">
                      {(mine || (canCoach && n.assignees.length > 0)) && (
                        <button
                          onClick={() => tick(n, !done)}
                          className="text-[11px] text-text-faint hover:text-accent"
                        >
                          {done ? 'mark not done' : 'mark done'}
                        </button>
                      )}
                      {(n.authorSid === user?.steamid64 || canCoach) && (
                        <>
                          <button
                            onClick={() => {
                              setEditing(n.id)
                              setDraft(n.body)
                            }}
                            className="text-[11px] text-text-faint hover:text-accent"
                          >
                            edit
                          </button>
                          <button onClick={() => remove(n)} className="text-[11px] text-text-faint hover:text-loss">
                            delete
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                  {editing === n.id ? (
                    <div className="mt-1">
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={3}
                        maxLength={2000}
                        autoFocus
                        className="w-full border border-border bg-bg-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
                        style={{ borderRadius: 'var(--radius)' }}
                      />
                      <div className="mt-1 flex gap-3 text-[11px]">
                        <button onClick={() => saveEdit(n)} className="text-accent hover:underline">
                          save
                        </button>
                        <button onClick={() => setEditing(null)} className="text-text-faint hover:text-text-dim">
                          cancel
                        </button>
                        {n.updatedAt && <span className="text-text-faint">edited {fmtStamp(n.updatedAt)}</span>}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-text-dim">
                      {n.body}
                      {n.updatedAt && (
                        <span className="ml-2 text-[10px] text-text-faint" title={fmtStamp(n.updatedAt)}>
                          (edited)
                        </span>
                      )}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
